import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import '@/App.css';
import useAuthStore from './store/authStore';

// Pages
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import Leads from './pages/Leads';
import Products from './pages/Products';
import Quotations from './pages/Quotations';
import CreateQuotation from './pages/CreateQuotation';
import QuotationDetail from './pages/QuotationDetail';
import Approvals from './pages/Approvals';
import UserManagement from './pages/UserManagement';
import ProductApprovals from './pages/ProductApprovals';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import LeadDetail from './pages/LeadDetail';
import LORDForm from './pages/LORDForm';
import ProductCompare from './pages/ProductCompare';
import ImportHistory from './pages/ImportHistory';
import Notifications from './pages/Notifications';
import CompanySettings from './pages/CompanySettings';
import Goals from './pages/Goals';
import CompetitionMapping from './pages/CompetitionMapping';
import Interactions from './pages/Interactions';
import Collections from './pages/Collections';
import MyNotifications from './pages/MyNotifications';
import Memos from './pages/Memos';
import GstExemptions from './pages/GstExemptions';
import ProformaInvoices from './pages/ProformaInvoices';
import Team from './pages/Team';
import ReviewCenter from './pages/reviews/ReviewCenter';
import CreateCycle from './pages/reviews/CreateCycle';
import CycleDetail from './pages/reviews/CycleDetail';
import CommitmentPage from './pages/reviews/CommitmentPage';
import PeriodDetail from './pages/reviews/PeriodDetail';
import Profile from './pages/Profile';
import AuditLogs from './pages/AuditLogs';
import ProductDetail from './pages/ProductDetail';
import GrossMargin from './pages/GrossMargin';
import TenderWorkload from './pages/TenderWorkload';
import DataQuality from './pages/DataQuality';
import Tenders from './pages/Tenders';
import TenderDetail from './pages/TenderDetail';
import AiFeedback from './pages/AiFeedback';
import FeasibilityProfile from './pages/FeasibilityProfile';
import PerformanceArchive from './pages/PerformanceArchive';
import DailyActivity from './pages/DailyActivity';
import SuperAdmin from './pages/SuperAdmin';
import AdminConsole from './pages/AdminConsole';

// Marketing (public — vooxy.app root domain)
import Landing from './pages/marketing/Landing';
import Pricing from './pages/marketing/Pricing';
import Contact from './pages/marketing/Contact';
import useIsRootDomain from './hooks/useIsRootDomain';

// Components
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import ConfirmDialog from './components/ConfirmDialog';
import { Toaster } from 'sonner';
import ErrorBoundary from './components/ErrorBoundary';

/**
 * Shell for every authenticated tenant route: <ProtectedRoute><Layout><Outlet />.
 * Kept as a single element so route definitions below stay one-liners.
 *
 * The <Outlet /> is wrapped in an ErrorBoundary so an uncaught render error
 * in any single page shows a visible message instead of a white screen, and
 * leaves the sidebar/navigation intact so the user can move on. `resetKey`
 * uses the pathname so navigation auto-clears any prior crash.
 */
function TenantAppShell() {
  const location = useLocation();
  return (
    <ProtectedRoute>
      <Layout>
        <ErrorBoundary resetKey={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </Layout>
    </ProtectedRoute>
  );
}

/**
 * Legacy-URL redirect. When a user hits `/dashboard` or `/leads/xyz` at the
 * root (bookmarks from the pre-Feb-2026 build), forward them to the same path
 * under `/t/<slug>/...` derived from the JWT. If no JWT is present, bounce
 * them to the marketing landing.
 */
function LegacyRedirect() {
  const location = useLocation();
  const { isAuthenticated } = useAuthStore();
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  let slug = '';
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      slug = (payload.tenant_slug || '').toLowerCase();
    } catch { /* ignore malformed token */ }
  }

  // Preserve the original path + query string when redirecting into the tenant.
  const target = `${location.pathname}${location.search}`;

  if (!isAuthenticated || !slug) return <Navigate to="/" replace />;
  return <Navigate to={`/t/${slug}${target}`} replace />;
}

function App() {
  const { isAuthenticated, fetchUser } = useAuthStore();
  const isRootDomain = useIsRootDomain();

  useEffect(() => {
    if (isAuthenticated) {
      fetchUser();
    }
  }, []);

  // Root path behaviour:
  //   Marketing host  → show Landing
  //   Tenant subdomain (future) → forward to their /t/<slug>/dashboard (via LegacyRedirect)
  const rootElement = isRootDomain ? <Landing /> : <LegacyRedirect />;

  return (
    <BrowserRouter>
      <ConfirmDialog />
      <Toaster position="top-right" richColors closeButton toastOptions={{ style: { fontFamily: 'inherit' } }} />
      <Routes>
        {/* ── Marketing (public, no tenant) ─────────────────────────── */}
        <Route path="/" element={rootElement} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/contact" element={<Contact />} />

        {/* ── Super-admin (platform, no tenant) ─────────────────────── */}
        <Route path="/vooxy-console" element={<AdminConsole />} />
        <Route path="/super-admin" element={
          isAuthenticated
            ? <Layout><SuperAdmin /></Layout>
            : <Navigate to="/vooxy-console" replace />
        } />

        {/* ── Tenant-scoped app: /t/:tenantSlug/... ─────────────────── */}
        <Route path="/t/:tenantSlug">
          {/* Public within tenant */}
          <Route path="login" element={<Login />} />
          <Route path="signup" element={<Signup />} />

          {/* Protected tenant app */}
          <Route element={<TenantAppShell />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="leads" element={<Leads />} />
            <Route path="leads/:id" element={<LeadDetail />} />
            <Route path="leads/:id/lord" element={<LORDForm />} />
            <Route path="products" element={<Products />} />
            <Route path="products/compare" element={<ProductCompare />} />
            <Route path="products/:id" element={<ProductDetail />} />
            <Route path="quotations" element={<Quotations />} />
            <Route path="quotations/create" element={<CreateQuotation />} />
            <Route path="quotations/:id" element={<QuotationDetail />} />
            <Route path="approvals" element={<Approvals />} />
            <Route path="company-settings" element={<CompanySettings />} />
            <Route path="memos" element={<Memos />} />
            <Route path="gst-exemptions" element={<GstExemptions />} />
            <Route path="proforma-invoices" element={<ProformaInvoices />} />
            <Route path="team" element={<Team />} />
            <Route path="team/daily-activity" element={<DailyActivity />} />
            <Route path="reviews" element={<ReviewCenter />} />
            <Route path="reviews/new" element={<CreateCycle />} />
            <Route path="reviews/cycles/:cycleId" element={<CycleDetail />} />
            <Route path="reviews/commit/:periodId" element={<CommitmentPage />} />
            <Route path="reviews/periods/:periodId" element={<PeriodDetail />} />
            <Route path="my-notifications" element={<MyNotifications />} />
            <Route path="goals" element={<Goals />} />
            <Route path="competition" element={<CompetitionMapping />} />
            <Route path="interactions" element={<Interactions />} />
            <Route path="collections" element={<Collections />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="import-history" element={<ImportHistory />} />
            <Route path="product-approvals" element={<ProductApprovals />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="customers" element={<Customers />} />
            <Route path="customers/:id" element={<CustomerDetail />} />
            <Route path="profile" element={<Profile />} />
            <Route path="audit-logs" element={<AuditLogs />} />
            <Route path="gross-margin" element={<GrossMargin />} />
            <Route path="tender-workload" element={<TenderWorkload />} />
            <Route path="data-quality" element={<DataQuality />} />
            <Route path="tenders" element={<Tenders />} />
            <Route path="tenders/:id" element={<TenderDetail />} />
            <Route path="ai-feedback" element={<AiFeedback />} />
            {/* iter116 — Feasibility Profile & Performance Archive live
                under the Tenders parent in the sidebar. Canonical URLs are
                the nested ones; the flat legacy paths auto-redirect to
                the nested versions so bookmarks + old notification links
                keep working. */}
            <Route path="tenders/feasibility-profile" element={<FeasibilityProfile />} />
            <Route path="tenders/performance-archive" element={<PerformanceArchive />} />
            <Route path="feasibility-profile" element={<Navigate to="../tenders/feasibility-profile" replace />} />
            <Route path="performance-archive" element={<Navigate to="../tenders/performance-archive" replace />} />
          </Route>
        </Route>

        {/* ── Legacy paths — redirect old bookmarks into the tenant path ─
              These match paths like /dashboard, /leads/abc, etc. and forward
              them to /t/<slug>/dashboard etc. using the JWT-embedded slug. */}
        <Route path="/login" element={<LegacyRedirect />} />
        <Route path="/signup" element={<LegacyRedirect />} />
        <Route path="/dashboard/*" element={<LegacyRedirect />} />
        <Route path="/leads/*" element={<LegacyRedirect />} />
        <Route path="/products/*" element={<LegacyRedirect />} />
        <Route path="/quotations/*" element={<LegacyRedirect />} />
        <Route path="/approvals" element={<LegacyRedirect />} />
        <Route path="/company-settings" element={<LegacyRedirect />} />
        <Route path="/memos" element={<LegacyRedirect />} />
        <Route path="/gst-exemptions" element={<LegacyRedirect />} />
        <Route path="/proforma-invoices" element={<LegacyRedirect />} />
        <Route path="/team" element={<LegacyRedirect />} />
        <Route path="/my-notifications" element={<LegacyRedirect />} />
        <Route path="/goals" element={<LegacyRedirect />} />
        <Route path="/notifications" element={<LegacyRedirect />} />
        <Route path="/import-history" element={<LegacyRedirect />} />
        <Route path="/product-approvals" element={<LegacyRedirect />} />
        <Route path="/users" element={<LegacyRedirect />} />
        <Route path="/customers/*" element={<LegacyRedirect />} />
        <Route path="/profile" element={<LegacyRedirect />} />
        <Route path="/audit-logs" element={<LegacyRedirect />} />
        <Route path="/gross-margin" element={<LegacyRedirect />} />
        <Route path="/data-quality" element={<LegacyRedirect />} />
        <Route path="/tenders" element={<LegacyRedirect />} />
        <Route path="/tenders/:id" element={<LegacyRedirect />} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
